import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { getAttributesByStep } from "../../reducers/project/selectors";
import { IAttributes, Steps, Types } from "../../reducers/project";
import Attribute from "./Attribute";
import styled from "styled-components";
import { ProjectActions } from "../../reducers/project/actionsNames";
import { groupBy } from "lodash";
import Scrollbars from "react-custom-scrollbars";
import content from "../../content";
import useDesktop  from "../Responsive/useDesktop";

const AttributesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: 15px 0px;
`;

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  heading: {
    fontSize: "14px",
    fontWeight: 600,
    color: "black",
  },
  expansionHeader: {
    maxHeight: "40px",
    backgroundColor: "#F5F4F4",
    border: "1px solid #EAE5E5",
  },
  expansionHeaderExpanded: {
    minHeight: "40px !important",
  },
  expansionBody: {
    border: "1px solid #EAE5E5",
    backgroundColor: "white",
  },
}));

export const AttributeContainer = styled.div<{
  border: boolean;
  center: boolean;
}>`
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  justify-content: ${({ center }) => (center ? "center" : "start")};
  padding-top: 20px;
`;

export const AttributeHeader = styled.div`
  font-family: Inter;
  display: flex;
  align-items: center;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  color: #1e2029;
  padding: 15px 15px 15px 0;
  text-transform: capitalize;
  letter-spacing: -0.01em;
  border-bottom: 2px solid #efa21e;
`;

const Wrapper = styled.div<any>`
  height: 100%;
  background: white;
  padding: 0 0px 25px 25px;
  margin-left: ${({ step }) => (step ? "0" : "5px")};
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 1530px) {
    padding: 0 10px 10px 10px;
  }

  @media (max-width: 1023px) {
    padding: 0 18px 18px 18px;
  }
`;

const PaddingWrapper = styled.div`
  padding-right: 25px;

  @media (max-width: 1530px) {
    padding-right: 0px;
  }
`;

function AttributesExpansion({
                               name,
                               pos,
                               expanded,
                               toggleExpand,
                               last,
                               onClick,
                               expandNext,
                               setScrollTop,
                               setScrollMax,
                               scrollTop,
                               scrollMax,
                               bgcolor,
                               step,
                             }: IAttributesExpansion) {
  const [search, setSearch] = useState<string>("");
  const scrollRef = useRef<any>();

  const classes = useStyles();
  const desktop = useDesktop();

  const attributes = useSelector(
      getAttributesByStep((step as unknown) as Steps)
  );

  const dispatch = useDispatch();

  const onExpand = () => {
    if (toggleExpand) {
      toggleExpand(step as any);
    }
  };

  const selectAttr = (attr: IAttributes) => () => {
    if (onClick) {
      onClick(attr);
    }
    dispatch({ type: ProjectActions.SelectAttr, attr });
  };

  const attributesGrouped = groupBy(attributes, "category");

  useEffect(() => {
    if (scrollRef.current) {
      const scrollHeight = scrollRef.current.getScrollHeight();
      if (scrollHeight > scrollMax) {
        setScrollMax(scrollHeight);
      }
    }
  }, [scrollRef.current]);

  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop(scrollTop);
    }
  }, [scrollTop]);

  const onScroll = (values) => {
    setScrollTop(values.scrollTop);
  };

  const contents = Object.entries(attributesGrouped).map(([key, attrG], i) => {
    return (
        <div>
          <AttributeHeader>{content.getValue(key)}</AttributeHeader>
          <AttributeContainer
              center={!attrG.some((ag) => ag.type === Types.Input)}
              border={Object.entries(attributesGrouped).length - 1 != i}
          >
            <div
                style={{
                  display: "flex",
                  minWidth: 0,
                  flexWrap: "wrap",
                  width:
                      attrG.some((ag) => ag.type == Types.Picker) || !desktop
                          ? "100%"
                          : "200px",
                }}
            >
              {attrG.map((a) => (
                  <Attribute onClick={selectAttr(a)} key={content.getValue(a.name)} attr={a} />
              ))}
            </div>
          </AttributeContainer>
        </div>
    );
  });

  return (
      <div
          style={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            boxSizing: "border-box",
            marginTop: "20px",
            minWidth: "0",
            marginLeft: step === 0 && desktop ? "20px" : "0",
          }}
      >
        <Header bgcolor={bgcolor} name={name} step={step} />
        <Wrapper step={step === 0}>
          {desktop && (
              <Scrollbars
                  renderTrackVertical={step === 3 ? undefined : () => <div></div>}
                  hideTracksWhenNotNeeded={true}
                  autoHide={true}
                  onScrollFrame={onScroll}
                  ref={scrollRef}
              >
                <PaddingWrapper
                    style={{
                      height: scrollMax !== 0 ? scrollMax + "px" : "unset",
                    }}
                >
                  {contents}
                </PaddingWrapper>
              </Scrollbars>
          )}
          {!desktop && <>{contents}</>}
        </Wrapper>
      </div>
  );
}

const Label = styled.div`
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 0.05em;
  color: white;
  opacity: 0.7;
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
`;

const WrapperHeader = styled.div<any>`
  width: ${({ step }) => (step ? "100%" : "calc(100% - 5px)")};
  display: flex;
  box-sizing: border-box;
  min-width: 0;
  justify-content: center;
  padding: 16px 0px;
  margin-left: ${({ step }) => (step ? "0" : "5px")};
`;

const Header = ({ name, step, bgcolor }) => {
  return (
      <WrapperHeader
          step={step === 0}
          style={{
            backgroundColor: bgcolor,
            borderRadius:
                step === 3
                    ? "0px 4px 0px 0px"
                    : step === 0
                        ? "4px 0px 0px 0px"
                        : "unset",
          }}
      >
        <Label>{name}</Label>
      </WrapperHeader>
  );
};

export interface IAttributesExpansion {
  name: string;
  pos?: number;
  expanded?: boolean;
  last?: boolean;
  toggleExpand?: (name: string) => void;
  expandNext?: (name: string) => () => void;
  onClick?: any;
  step: Steps | string;
  bgcolor?: string;
  setScrollTop?: any;
  setScrollMax?: any;
  scrollMax: number;
  scrollTop: number;
}

export default AttributesExpansion;
